import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileSpreadsheet, Plus, Upload, Trash2, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";

const TARGETS = ["zoho_people_employee", "zoho_payroll_employee", "zoho_payroll_salary", "zoho_people_shift", "generic"];
const DATE_FORMATS = ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "DD-MMM-YYYY"];
const TRANSFORMS = ["none", "uppercase", "date", "boolean", "value_map"];

export default function ExportTemplates() {
  const { user, actingOrgId } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [editing, setEditing] = useState(null);

  const { data: templates = [] } = useQuery({
    queryKey: ["templates", actingOrgId],
    queryFn: async () => (await api.get("/exports/templates")).data,
  });
  const { data: catalog = [] } = useQuery({
    queryKey: ["catalog"], queryFn: async () => (await api.get("/exports/field-catalog")).data,
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/exports/templates/${id}`)).data,
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["templates"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const clone = useMutation({
    mutationFn: async (id) => (await api.post(`/exports/templates/${id}/clone`)).data,
    onSuccess: () => { toast.success("Cloned to this org"); qc.invalidateQueries({ queryKey: ["templates"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const fromSample = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/exports/templates/from-sample", fd, { params: { name: file.name.replace(/\.[^.]+$/, "") } });
      toast.success("Template created from sample");
      qc.invalidateQueries({ queryKey: ["templates"] });
      setEditing(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const newTemplate = () => setEditing({ name: "New template", target: "generic", file_type: "xlsx", date_format: "DD-MM-YYYY", columns: [] });
  const canEdit = (t) => t.scope === "org" || (user?.roles?.includes("super_admin") && !actingOrgId);

  return (
    <div>
      <PageHeader title="Export Templates" description="Zoho headers vary by account — every export is driven by an editable template"
        actions={
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="sample-input"
              onChange={(e) => { if (e.target.files?.[0]) fromSample(e.target.files[0]); e.target.value = ""; }} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="from-sample"><Upload className="mr-2 h-4 w-4" /> From Zoho sample</Button>
            <Button onClick={newTemplate} data-testid="new-template"><Plus className="mr-2 h-4 w-4" /> New</Button>
          </div>
        } />

      {templates.length === 0 ? <EmptyState title="No templates" icon={FileSpreadsheet} /> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <Card key={t.id} className="p-5" data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between">
                <span className={`rounded px-2 py-0.5 text-[10px] font-medium ${t.scope === "global" ? "bg-indigo-50 text-indigo-600" : "bg-emerald-50 text-emerald-600"}`}>{t.scope}</span>
                <div className="flex gap-1">
                  {t.scope === "global" && actingOrgId && <Button size="sm" variant="ghost" onClick={() => clone.mutate(t.id)} data-testid={`clone-${t.id}`}><Copy className="h-3.5 w-3.5" /></Button>}
                  {canEdit(t) && <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                  {canEdit(t) && <Button size="sm" variant="ghost" onClick={() => del.mutate(t.id)}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>}
                </div>
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-800">{t.name}</p>
              <p className="text-xs text-slate-400">{t.target} · {t.file_type} · {t.columns?.length || 0} columns</p>
            </Card>
          ))}
        </div>
      )}

      {editing && <TemplateEditor template={editing} catalog={catalog} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["templates"] }); }} />}
    </div>
  );
}

function TemplateEditor({ template, catalog, onClose, onSaved }) {
  const [t, setT] = useState(template);
  const isNew = !template.id;

  const setCol = (i, k, v) => setT({ ...t, columns: t.columns.map((c, x) => (x === i ? { ...c, [k]: v } : c)) });
  const addCol = () => setT({ ...t, columns: [...t.columns, { header: "", source: "", transform: "none", default: "", required: false }] });
  const delCol = (i) => setT({ ...t, columns: t.columns.filter((_, x) => x !== i) });

  const save = async () => {
    try {
      if (isNew) await api.post("/exports/templates", t);
      else await api.put(`/exports/templates/${t.id}`, t);
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="template-editor">
        <DialogHeader><DialogTitle>{isNew ? "New template" : "Edit template"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <div><Label className="text-xs">Name</Label><Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} className="mt-1" data-testid="tpl-name" /></div>
          <div><Label className="text-xs">Target</Label>
            <Select value={t.target} onValueChange={(v) => setT({ ...t, target: v })}>
              <SelectTrigger className="mt-1" data-testid="tpl-target"><SelectValue /></SelectTrigger>
              <SelectContent>{TARGETS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">File type</Label>
            <Select value={t.file_type} onValueChange={(v) => setT({ ...t, file_type: v })}>
              <SelectTrigger className="mt-1" data-testid="tpl-filetype"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="xlsx">xlsx</SelectItem><SelectItem value="csv">csv</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-xs">Date format</Label>
            <Select value={t.date_format} onValueChange={(v) => setT({ ...t, date_format: v })}>
              <SelectTrigger className="mt-1" data-testid="tpl-dateformat"><SelectValue /></SelectTrigger>
              <SelectContent>{DATE_FORMATS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>

        <div className="mt-2">
          <div className="mb-2 flex items-center justify-between">
            <Label className="text-xs font-semibold">Columns ({t.columns.length})</Label>
            <Button size="sm" variant="outline" onClick={addCol} data-testid="add-column"><Plus className="mr-1 h-3.5 w-3.5" /> Column</Button>
          </div>
          <div className="space-y-2">
            {t.columns.map((c, i) => (
              <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-lg border border-slate-100 bg-slate-50/50 p-2" data-testid={`col-${i}`}>
                <div className="col-span-3"><Label className="text-[10px]">Header</Label><Input value={c.header} onChange={(e) => setCol(i, "header", e.target.value)} className="h-8 text-xs" data-testid={`col-header-${i}`} /></div>
                <div className="col-span-3"><Label className="text-[10px]">Source field</Label>
                  <Select value={c.source || "none"} onValueChange={(v) => setCol(i, "source", v === "none" ? "" : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`col-source-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="max-h-72"><SelectItem value="none">— blank —</SelectItem>
                      {catalog.map((f) => <SelectItem key={f.field} value={f.field}>{f.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="col-span-2"><Label className="text-[10px]">Transform</Label>
                  <Select value={c.transform || "none"} onValueChange={(v) => setCol(i, "transform", v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRANSFORMS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="col-span-2"><Label className="text-[10px]">Default</Label><Input value={c.default || ""} onChange={(e) => setCol(i, "default", e.target.value)} className="h-8 text-xs" /></div>
                <div className="col-span-1 flex items-center justify-center pb-1">
                  <input type="checkbox" checked={!!c.required} onChange={(e) => setCol(i, "required", e.target.checked)} title="Required" data-testid={`col-req-${i}`} />
                </div>
                <div className="col-span-1 pb-1"><button onClick={() => delCol(i)}><Trash2 className="h-4 w-4 text-rose-400" /></button></div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter><Button onClick={save} data-testid="save-template">Save template</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
